import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/auth";
import { Menu } from "lucide-react";
import Sidebar from "../sidebar/Sidebar";
import SideNavBar from "../sideNavBar/SideNavBar";
import "./Header.css";

const Header = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallTablet, setIsSmallTablet] = useState(false);
  const [greeting, setGreeting] = useState("");
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeNav, setActiveNav] = useState("explore");

  const userName =
    location.state?.userName ||
    (user?.firstName
      ? `${user.firstName} ${user.lastName || ""}`.trim()
      : "Guest User");

  const userPhoto = user?.userPhoto || "https://via.placeholder.com/40";

  const isAuthenticated = !!user;
  const hasActiveRole =
    user?.activeRole && ["host", "guest", "usher"].includes(user.activeRole);
  const isFullyAuthenticated = isAuthenticated && hasActiveRole;

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsSmallTablet(width >= 768 && width < 1024);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const updateGreeting = () => {
      const currentHour = new Date().getHours();
      if (currentHour >= 5 && currentHour < 12) {
        setGreeting("Good Morning");
      } else if (currentHour >= 12 && currentHour < 18) {
        setGreeting("Good Afternoon");
      } else {
        setGreeting("Good Evening");
      }
    };
    updateGreeting();
    const greetingInterval = setInterval(updateGreeting, 60000);
    return () => clearInterval(greetingInterval);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleNavClick = (path) => {
    navigate(path);
    setActiveNav(path.replace("/", "") || "home");
  };

const handleLoginClick = () => {
  navigate("/onboarding", { state: { mode: "login" } });
};

const handleCreateAccountClick = () => {
  navigate("/onboarding", { state: { mode: "signup" } });
};

  const getCurrentDate = () => {
    const date = new Date();
    const day = date.getDate();
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}${getDaySuffix(day)} ${month}, ${year}`;
  };

  const getDaySuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const shouldShowMobileHeader = isMobile || isSmallTablet;

  return (
    <>
      {shouldShowMobileHeader ? (
        <div className="header-nav-mobile">
          <div className="header-nav-mobile-top-bar">
            <div className="header-nav-mobile-logo">
              <img
                src={"/images/logoOnBlack.png"}
                alt="Logo"
                onClick={() => navigate("/")}
              />
            </div>
            <div className="header-nav-mobile-right">
              {isFullyAuthenticated && (
                <div className="header-nav-notification-bell">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.36 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.63 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z"
                      fill="#333"
                    />
                  </svg>
                </div>
              )}

              {isFullyAuthenticated && (
                <>
                  <div className="header-nav-mobile-avatar">
                    <img
                      src={user?.userPhoto || "/images/avatar.webp"}
                      alt="User avatar"
                    />
                  </div>
                  <div
                    className="header-nav-mobile-menu"
                    onClick={() => setSidebarVisible(!sidebarVisible)}
                  >
                    <div className="header-nav-hamburger">
                      <Menu size={28} />
                      <div className="header-nav-menu-text">Menu</div>
                    </div>
                  </div>
                </>
              )}

              {!isFullyAuthenticated && (
                <div
                  className="header-nav-mobile-menu"
                  onClick={toggleSidebar}
                  style={{ cursor: "pointer" }}
                >
                  <div className="header-nav-hamburger">
                    <Menu size={28} />
                    <div className="header-nav-menu-text">Menu</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="header-nav-mobile-content">
            <div className="header-nav-mobile-greeting">
              <h1>Hello, {userName}</h1>
              <p>{greeting}, How are you today?</p>
            </div>
            <div className="header-nav-mobile-date">
              <p className="header-nav-date-label">Today's Date</p>
              <p className="header-nav-date-value">{getCurrentDate()}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="header-nav-desktop">
          <div className="header-nav-desktop-greeting">
            <h1>Hello, {userName} ⛅</h1>
            <p>{greeting}, How are you today?</p>
          </div>
          <div className="header-nav-desktop-avatar">
            <img
              src={user?.userPhoto || "/images/avatar.webp"}
              alt="User avatar"
            />
          </div>
        </div>
      )}

      {sidebarVisible && shouldShowMobileHeader && isFullyAuthenticated && (
        <div
          className="header-nav-mobile-sidebar-overlay"
          onClick={() => setSidebarVisible(false)}
        >
          <div
            className="header-nav-mobile-sidebar"
            onClick={(e) => e.stopPropagation()}
          >
            <Sidebar />
          </div>
        </div>
      )}

      {!isFullyAuthenticated && (
        <SideNavBar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          activeNav={activeNav}
          onNavClick={handleNavClick}
          onLoginClick={handleLoginClick}
          onCreateAccountClick={handleCreateAccountClick}
        />
      )}
    </>
  );
};

export default Header;